import { useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Flex,
  Select,
  Badge,
  Tooltip,
  IconButton,
  useToast,
  Text,
  HStack,
  Heading,
  Spacer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  VStack,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import { SearchIcon, ViewIcon } from '@chakra-ui/icons';
import { FaSortUp, FaSortDown, FaQuestionCircle, FaTimes } from 'react-icons/fa';
import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '../../supabaseClient';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { useMenuTitle } from '../../hooks/useMenuTitle';
import { validateEmailAddress } from '../../utils/emailAddressValidation';
import { validatePhoneNumber } from '../../utils/phoneNumberValidation';

const PAGE_SIZE = 50;

const ImportHistoryLog = () => {
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [removedFilter, setRemovedFilter] = useState('all');
  const [transferModeFilter, setTransferModeFilter] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [selectedImport, setSelectedImport] = useState(null);
  const searchTimeout = useRef(null);
  const location = useLocation();
  const menuItem = location.state?.menuItem;
  const menuTitle = useMenuTitle();

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  // Build the query without search filtering
  const buildQuery = useCallback((pageParam = 0) => {
    return supabase
      .from('import_history_view')
      .select(`
        id,
        first_name,
        last_name,
        email,
        phone_number,
        cleaned_phone_number,
        created_at,
        is_processed,
        has_phone_conflict,
        import_batch_id,
        has_missing_fields,
        has_invalid_phone,
        has_invalid_email,
        missing_fields,
        changed_fields,
        is_removed,
        transfer_mode,
        creator_name
      `)
      .range(pageParam * PAGE_SIZE, (pageParam + 1) * PAGE_SIZE - 1);
  }, []);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error
  } = useInfiniteQuery({
    queryKey: ['importHistory'],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, error, count } = await buildQuery(pageParam);
      if (error) throw error;
      return { data, count };
    },
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage.count) return undefined;
      return pages.length * PAGE_SIZE < lastPage.count ? pages.length : undefined;
    }
  });

  // Filter and sort the data client-side
  const filteredData = useMemo(() => {
    if (!data?.pages) return [];

    let result = data.pages.flatMap(page => page.data || []);

    // Add validation check
    result = result.map(record => ({
      ...record,
      has_invalid_email: record.email && !validateEmailAddress(record.email, false),
      has_invalid_phone: record.phone_number && !validatePhoneNumber(record.phone_number, false)
    }));

    // Debug logging for removed records with full details
    console.log('Records with is_removed values:', result.map(r => ({
      id: r.id,
      is_removed: r.is_removed,
      typeOf: typeof r.is_removed,
      stringified: JSON.stringify(r.is_removed)
    })));

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      result = result.filter(record =>
        record.first_name?.toLowerCase().includes(lowerSearchTerm) ||
        record.last_name?.toLowerCase().includes(lowerSearchTerm) ||
        record.email?.toLowerCase().includes(lowerSearchTerm) ||
        record.phone_number?.includes(searchTerm)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(record => record.is_processed === (statusFilter === 'processed'));
    }

    // Filter by removed status
    if (removedFilter !== 'all') {
      // Debug logging for removed filter
      console.log('Filtering removed records:', { 
        filter: removedFilter,
        beforeCount: result.length,
        recordsWithTrueRemoved: result.filter(r => r.is_removed === true).length,
        sampleRecords: result.slice(0, 5).map(r => ({
          id: r.id,
          first_name: r.first_name,
          last_name: r.last_name,
          is_removed: r.is_removed,
          typeOf: typeof r.is_removed
        }))
      });

      result = result.filter(record => {
        const isRemoved = record.is_removed;
        console.log('Processing record:', {
          id: record.id,
          name: `${record.first_name} ${record.last_name}`,
          is_removed: isRemoved,
          typeOf: typeof isRemoved,
          filterValue: removedFilter,
          willKeep: removedFilter === 'removed' ? isRemoved : !isRemoved
        });
        
        return removedFilter === 'removed' ? isRemoved : !isRemoved;
      });

      // Debug logging after filter
      console.log('After removed filter:', { 
        resultCount: result.length,
        keptRecords: result.map(r => ({
          id: r.id,
          name: `${r.first_name} ${r.last_name}`,
          is_removed: r.is_removed
        }))
      });
    }

    // Filter by transfer mode
    if (transferModeFilter !== 'all') {
      result = result.filter(record => {
        if (transferModeFilter === 'unassigned') {
          return !record.transfer_mode;
        }
        return record.transfer_mode === transferModeFilter;
      });
    }

    return result;
  }, [data?.pages, searchTerm, sortField, sortDirection, statusFilter, removedFilter, transferModeFilter]);

  const SortIcon = ({ field }) => {
    if (field === sortField) {
      return (
        <Icon as={sortDirection === 'asc' ? FaSortUp : FaSortDown} ml={1} />
      );
    }
    return (
      <Box as="span" ml={1} position="relative" display="inline-block" w="8px" h="12px">
        <Icon as={FaSortUp} position="absolute" top="0" left="0" />
        <Icon as={FaSortDown} position="absolute" bottom="0" left="0" />
      </Box>
    );
  };

  if (isError) {
    toast({
      title: 'Error loading import history',
      description: error.message,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  }

  const formatChangedFields = (changedFields) => {
    console.log('Raw changed_fields:', changedFields);
    if (!changedFields || typeof changedFields !== 'object') {
      console.log('No changed_fields or invalid type');
      return [];
    }
    
    try {
      // Create a single change object with all field changes
      const formattedChanges = [{
        timestamp: new Date().toISOString(),
        old: {},
        new: {}
      }];

      // Iterate through each field in changed_fields
      Object.entries(changedFields).forEach(([field, changes]) => {
        formattedChanges[0].old[field] = changes.old;
        formattedChanges[0].new[field] = changes.new;
      });

      console.log('Formatted changes:', formattedChanges);
      return formattedChanges;
    } catch (error) {
      console.error('Error parsing changed_fields:', error);
      return [];
    }
  };

  const getMissingFields = (record) => {
    const missingFields = [];
    if (!record.first_name) missingFields.push('First Name');
    if (!record.last_name) missingFields.push('Last Name');
    if (!record.email) missingFields.push('Email');
    if (!record.phone_number) missingFields.push('Phone');
    return missingFields;
  };

  return (
    <Box p={4}>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">{menuTitle}</Heading>
        <IconButton
          icon={<FaQuestionCircle />}
          aria-label="Help"
          size="sm"
          variant="ghost"
          onClick={() => setShowHelpModal(true)}
        />
      </HStack>

      <Flex gap={2} mb={2} wrap="wrap">
        <InputGroup size="sm" flex="1">
          <InputLeftElement>
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            size="sm"
          />
          {searchTerm && (
            <InputRightElement>
              <IconButton
                icon={<FaTimes />}
                size="xs"
                variant="ghost"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              />
            </InputRightElement>
          )}
        </InputGroup>

        <Select
          w="150px"
          size="sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="processed">Processed</option>
          <option value="pending">Pending</option>
        </Select>

        <Select
          w="150px"
          size="sm"
          value={removedFilter}
          onChange={(e) => setRemovedFilter(e.target.value)}
        >
          <option value="all">All Records</option>
          <option value="removed">Removed Only</option>
          <option value="not_removed">Active Only</option>
        </Select>

        <Select
          w="150px"
          size="sm"
          value={transferModeFilter}
          onChange={(e) => setTransferModeFilter(e.target.value)}
        >
          <option value="all">All Modes</option>
          <option value="unassigned">Unassigned</option>
          <option value="test">Test</option>
          <option value="production">Production</option>
          <option value="simulation">Simulation</option>
        </Select>
      </Flex>

      <Box position="relative" overflow="auto" maxH="calc(100vh - 250px)">
        <Table variant="simple" size="xs">
          <Thead position="sticky" top={0} bg="white" zIndex={1}>
            <Tr>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('first_name')}>
                <HStack spacing={1} display="inline-flex" alignItems="center">
                  <Text>NAME</Text>
                  <SortIcon field="first_name" />
                </HStack>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('email')}>
                <HStack spacing={1} display="inline-flex" alignItems="center">
                  <Text>EMAIL</Text>
                  <SortIcon field="email" />
                </HStack>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('phone_number')}>
                <HStack spacing={1} display="inline-flex" alignItems="center">
                  <Text>PHONE</Text>
                  <SortIcon field="phone_number" />
                </HStack>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('cleaned_phone_number')}>
                <HStack spacing={1} display="inline-flex" alignItems="center">
                  <Text>CLEANED</Text>
                  <SortIcon field="cleaned_phone_number" />
                </HStack>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('transfer_mode')}>
                <Tooltip label="Transfer mode indicates the state of the import: Unassigned (new import), Test (testing transfer), Production (live transfer), or Simulation (simulated transfer)">
                  <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Text>MODE</Text>
                    <SortIcon field="transfer_mode" />
                  </HStack>
                </Tooltip>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('is_processed')}>
                <HStack spacing={1} display="inline-flex" alignItems="center">
                  <Text>STATUS</Text>
                  <SortIcon field="is_processed" />
                </HStack>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" maxW="40px" cursor="pointer" onClick={() => handleSort('has_phone_conflict')}>
                <Tooltip label="Phone number exists in contacts database">
                  <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Text>📱</Text>
                    <SortIcon field="has_phone_conflict" />
                  </HStack>
                </Tooltip>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal">
                <Tooltip label="Missing Fields">
                  <Text>MISSING</Text>
                </Tooltip>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" maxW="40px">
                <Tooltip label="Invalid Phone">
                  <Text>📱❌</Text>
                </Tooltip>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal" maxW="40px">
                <Tooltip label="Invalid Email">
                  <Text>✉️❌</Text>
                </Tooltip>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal">
                <Tooltip label="Record Removed">
                  <Text>🗑️❌</Text>
                </Tooltip>
              </Th>
              <Th py={0} px={1} fontSize="2xs" fontWeight="normal">INFO</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredData.map((record) => (
              <Tr key={record.id}>
                <Td py={0} px={1} fontSize="xs" maxW="200px">
                  <Box overflowX="auto" whiteSpace="nowrap">
                    {`${record.first_name} ${record.last_name}`}
                  </Box>
                </Td>
                <Td py={0} px={1} fontSize="xs" maxW="200px">
                  <Box overflowX="auto" whiteSpace="nowrap">
                    {record.email || '-'}
                  </Box>
                </Td>
                <Td py={0} px={1} fontSize="xs" maxW="120px">
                  <Box overflowX="auto" whiteSpace="nowrap">
                    {record.phone_number}
                  </Box>
                </Td>
                <Td py={0} px={1} fontSize="xs" maxW="120px">
                  <Box overflowX="auto" whiteSpace="nowrap">
                    {record.cleaned_phone_number || '-'}
                  </Box>
                </Td>
                <Td py={0} px={1} fontSize="xs">
                  {record.transfer_mode}
                </Td>
                <Td py={0} px={1} fontSize="xs">
                  <Badge colorScheme={record.is_processed ? 'green' : 'yellow'}>
                    {record.is_processed ? 'Processed' : 'Pending'}
                  </Badge>
                </Td>
                <Td py={0} px={1} fontSize="xs">
                  {record.has_phone_conflict && (
                    <Tooltip label="Phone number exists in contacts database">
                      <Text>Yes</Text>
                    </Tooltip>
                  )}
                </Td>
                <Td py={0} px={1} fontSize="xs">
                  {record.has_missing_fields && (
                    <Box px={2} py={1}>
                      <Text fontSize="xs" color="red.500">
                        {getMissingFields(record).join(', ')}
                      </Text>
                    </Box>
                  )}
                </Td>
                <Td py={0} px={1} fontSize="xs" textAlign="center">
                  {record.has_invalid_phone && <Text color="red.500" fontWeight="bold">❌</Text>}
                </Td>
                <Td py={0} px={1} fontSize="xs" textAlign="center">
                  {record.has_invalid_email && <Text color="red.500" fontWeight="bold">❌</Text>}
                </Td>
                <Td py={0} px={1} fontSize="xs" textAlign="center">
                  {record.is_removed && <Text color="red.500" fontWeight="bold">❌</Text>}
                </Td>
                <Td py={0} px={1} fontSize="xs">
                  <HStack spacing={2}>
                    <Tooltip label="View Batch Info">
                      <IconButton
                        icon={<FaQuestionCircle />}
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          setSelectedImport(record);
                          setShowBatchModal(true);
                        }}
                      />
                    </Tooltip>
                    {record.changed_fields && (
                      <Tooltip label="View Changes">
                        <IconButton
                          icon={<ViewIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => {
                            setSelectedImport(record);
                            setShowDetailsModal(true);
                          }}
                        />
                      </Tooltip>
                    )}
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Modal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Contact Change History</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedImport && (
              <Box p={4}>
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text fontWeight="bold" fontSize="lg">
                      {selectedImport.first_name} {selectedImport.last_name}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Import Status: <Badge colorScheme={selectedImport.is_processed ? "green" : "yellow"}>{selectedImport.is_processed ? "Processed" : "Pending"}</Badge>
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Created by: {selectedImport.creator_name || 'Unknown'} on {format(new Date(selectedImport.created_at), 'MMM d, yyyy h:mm a')}
                    </Text>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontSize="md" fontWeight="medium" mb={2}>
                      Field Changes History
                    </Text>
                    <Text fontSize="sm" color="gray.600" mb={4}>
                      Below are the changes detected during the import process. Red text shows the original value, and green text shows the new value.
                    </Text>
                  </Box>

                  {console.log('selectedImport:', selectedImport)}
                  {formatChangedFields(selectedImport.changed_fields).map((change, index) => (
                    <Box key={index} p={4} bg="gray.50" borderRadius="md">
                      <Text fontSize="sm" color="gray.600" mb={2}>
                        Change recorded on {(() => {
                          try {
                            return format(new Date(change.timestamp), 'MMM d, yyyy h:mm a');
                          } catch (error) {
                            return 'Invalid Date';
                          }
                        })()}
                      </Text>
                      <Box>
                        {Object.entries(change.old).map(([field, oldValue]) => (
                          <Box key={field} mb={3}>
                            <Text fontWeight="medium" mb={1}>{field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Text>
                            <HStack spacing={2} align="center">
                              <Text color="red.500" textDecoration="line-through">
                                {oldValue || '(empty)'}
                              </Text>
                              <Text color="gray.500">→</Text>
                              <Text color="green.500">
                                {change.new[field] || '(empty)'}
                              </Text>
                            </HStack>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={showBatchModal} onClose={() => setShowBatchModal(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Batch Information</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedImport && (
              <VStack align="stretch" spacing={3}>
                <Box>
                  <Text fontWeight="bold">Batch ID</Text>
                  <Text fontSize="sm">{selectedImport.import_batch_id}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Import Date</Text>
                  <Text fontSize="sm">
                    {format(new Date(selectedImport.created_at), 'MMM d, yyyy h:mm a')}
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Created By</Text>
                  <Text fontSize="sm">{selectedImport.creator_name || 'Unknown'}</Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Help Modal */}
      <Modal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
        size="lg"
      >
        <ModalOverlay />
        <ModalContent maxH="calc(100vh - 100px)" h="600px">
          <ModalHeader>Import History Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto">
            <VStack align="stretch" spacing={4}>
              <Text>
                The Import History Log provides a comprehensive view of all contact import activities,
                including bulk imports and transfers. It helps you track the status, validity, and
                changes made to imported contacts.
              </Text>

              <Accordion allowMultiple>
                <AccordionItem>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontWeight="bold">
                        Overview & Features
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <UnorderedList spacing={1} pl={4}>
                      <ListItem>View all contact import attempts and their current status</ListItem>
                      <ListItem>Track changes made during imports</ListItem>
                      <ListItem>Monitor data quality with validation indicators</ListItem>
                      <ListItem>View batch information and import details</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontWeight="bold">
                        Search & Filtering
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <UnorderedList spacing={1} pl={4}>
                      <ListItem>Search by name, email, or phone number</ListItem>
                      <ListItem>Filter by status: All, Processed, or Pending</ListItem>
                      <ListItem>Filter by record state: All Records, Removed Only, or Active Only</ListItem>
                      <ListItem>Filter by transfer mode: All, Unassigned, Test, Production, or Simulation</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontWeight="bold">
                        Status Indicators
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <UnorderedList spacing={1} pl={4}>
                      <ListItem><Badge colorScheme="green">Processed</Badge> - Contact has been successfully imported</ListItem>
                      <ListItem><Badge colorScheme="yellow">Pending</Badge> - Import is not yet complete</ListItem>
                      <ListItem>📱❌ - Phone number is invalid</ListItem>
                      <ListItem>✉️❌ - Email address is invalid</ListItem>
                      <ListItem>🗑️❌ - Record has been removed</ListItem>
                      <ListItem>Missing Fields - Shows which required fields are missing</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontWeight="bold">
                        Actions & Information
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <UnorderedList spacing={1} pl={4}>
                      <ListItem>View Changes - See what fields were modified during import</ListItem>
                      <ListItem>Batch Info - View details about the import batch</ListItem>
                      <ListItem>Phone Conflict - Indicates if phone number exists in contacts database</ListItem>
                      <ListItem>Transfer Mode - Shows the environment where the import was processed</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontWeight="bold">
                        Best Practices
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <UnorderedList spacing={1} pl={4}>
                      <ListItem>Regularly check for invalid or missing data</ListItem>
                      <ListItem>Use filters to focus on specific import batches or issues</ListItem>
                      <ListItem>Review phone conflicts to prevent duplicate contacts</ListItem>
                      <ListItem>Monitor removed records to track data cleanup</ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ImportHistoryLog;
